import { Component, ReactNode } from 'react'
import { WifiOff, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'

interface NetworkErrorBoundaryProps {
  children: ReactNode
}

interface NetworkErrorBoundaryState {
  hasNetworkError: boolean
  isOnline: boolean
}

/**
 * Network Error Boundary
 *
 * Specifically handles network-related errors and offline states.
 * Shows user-friendly messages and retry options.
 *
 * Usage:
 * ```tsx
 * <NetworkErrorBoundary>
 *   <YourComponent />
 * </NetworkErrorBoundary>
 * ```
 */
export class NetworkErrorBoundary extends Component<
  NetworkErrorBoundaryProps,
  NetworkErrorBoundaryState
> {
  constructor(props: NetworkErrorBoundaryProps) {
    super(props)
    this.state = {
      hasNetworkError: false,
      isOnline: navigator.onLine,
    }
  }

  componentDidMount() {
    window.addEventListener('online', this.handleOnline)
    window.addEventListener('offline', this.handleOffline)
  }

  componentWillUnmount() {
    window.removeEventListener('online', this.handleOnline)
    window.removeEventListener('offline', this.handleOffline)
  }

  handleOnline = () => {
    this.setState({ isOnline: true, hasNetworkError: false })
  }

  handleOffline = () => {
    this.setState({ isOnline: false, hasNetworkError: true })
  }

  static getDerivedStateFromError(
    error: Error
  ): Partial<NetworkErrorBoundaryState> | null {
    // Check if error is network-related
    if (
      error.message.includes('fetch') ||
      error.message.includes('network') ||
      error.message.includes('Failed to fetch') ||
      error.name === 'NetworkError'
    ) {
      return { hasNetworkError: true }
    }
    return null
  }

  handleRetry = () => {
    this.setState({ hasNetworkError: false })
    window.location.reload()
  }

  render() {
    if (!this.state.isOnline || this.state.hasNetworkError) {
      return (
        <div className="flex min-h-[400px] items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="mb-2 flex justify-center">
                <WifiOff className="h-12 w-12 text-muted-foreground" />
              </div>
              <CardTitle className="text-center">
                {!this.state.isOnline
                  ? 'No Internet Connection'
                  : 'Network Error'}
              </CardTitle>
              <CardDescription className="text-center">
                {!this.state.isOnline
                  ? 'Please check your internet connection and try again.'
                  : 'Unable to connect to the server. Please check your connection and try again.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={this.handleRetry}
                variant="default"
                className="w-full"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry Connection
              </Button>
            </CardContent>
          </Card>
        </div>
      )
    }

    return this.props.children
  }
}
